import Icon, { DownOutlined } from '@ant-design/icons';
import { Breadcrumb, Button, Col, Dropdown, Form, Input, message, Modal, Row, Select, Space, Tabs, Tag } from 'antd';
import TextArea from 'antd/lib/input/TextArea';
import React, { useContext, useEffect, useState } from 'react';
import { FormattedMessage, useIntl } from 'react-intl';
import { Link, useHistory, useParams } from 'react-router-dom';
import connectionApi from '../../../../api/ConnectionApi';
import hubApi from '../../../../api/HubApi';
import LayoutComponent from '../../../../components/LayoutComponent/LayoutComponent';
import CustomContext from '../../../../context/CustomContext';
import { Connection, Hub } from '../../../../model/entities';
import { ReactComponent as SaveSvg } from '../../../../resources/images/save.svg';
import notificationService from '../../../../services/NotificationService';
import providerAuthService from '../../../../services/ProviderAuthService';
import rolesService from '../../../../services/RolesService';
import stringService from '../../../../services/StringService';
import ConnectionCloneModal from '../../ConnectionsPage/ConnectionPage/ConnectionCloneModal/ConnectionCloneModal';
import ConnectionSchedulesComponent from '../../ConnectionsPage/ConnectionPage/ConnectionSchedulesComponent/ConnectionSchedulesComponent';
import ConnectionTasksComponent from '../../ConnectionsPage/ConnectionPage/ConnectionTasksComponent/ConnectionTasksComponent';
import ConnectionPermissionLinksComponent from './ConnectionPermissionLinksComponent/ConnectionPermissionLinksComponent';
import styles from './ConnectionPermissionPage.module.scss';

/**
 * Returns the connection permission page.
 * @returns the connection permission page.
 */
const ConnectionPermissionPage = (): React.ReactElement => {
    /*** HOOKS ***/

    const [form] = Form.useForm();
    const intl = useIntl();
    const params = useParams<ParamsType>();
    const history = useHistory();
    const context = useContext(CustomContext);
    const { auth } = context;
    const organizationId = auth!.organization!.id;
    const [connectionPermission, setConnectionPermission] = useState<Connection>();
    const [hubs, setHubs] = useState<Hub[]>([]);
    const [connectionPermissionCloneModalVisible, setConnectionPermissionCloneModalVisible] = useState<boolean>();
    const [saving, setSaving] = useState<boolean>();
    const [loading, setLoading] = useState<false | 'deleting'>();

    /*** EFFECTS ***/

    useEffect(() => {
        const init = async () => {
            try {
                if (params.id === 'new') {
                    const connectionPermission: Connection = { organizationId, connectionType: 'PERMISSION' };
                    const hubs = await hubApi.list(true);
                    setConnectionPermission(connectionPermission);
                    setHubs(hubs);
                    form.setFieldsValue(connectionPermission);
                } else {
                    const responses = await Promise.all([connectionApi.get(+params.id), hubApi.list(true)]);
                    const connectionPermission = responses[0];
                    const hubs = responses[1];

                    const sourceConnected =
                        connectionPermission.source &&
                        (await providerAuthService.isConnected(connectionPermission.source.provider));
                    const targetConnected =
                        connectionPermission.target &&
                        (await providerAuthService.isConnected(connectionPermission.target.provider));
                    if (!sourceConnected) {
                        Modal.warning({
                            title: intl.formatMessage({
                                id: `connectionPermission.modal.${connectionPermission.source?.provider}.title`,
                            }),
                            content: (
                                <p>
                                    {intl.formatMessage({
                                        id: `connectionPermission.modal.${connectionPermission.source?.provider}.description`,
                                    })}
                                </p>
                            ),
                            onOk: () => history.goBack(),
                            okText: intl.formatMessage({
                                id: `button.back`,
                            }),
                        });
                    } else if (!targetConnected) {
                        Modal.warning({
                            title: intl.formatMessage({
                                id: `connectionPermission.modal.${connectionPermission.target?.provider}.title`,
                            }),
                            content: (
                                <p>
                                    {intl.formatMessage({
                                        id: `connectionPermission.modal.${connectionPermission.target?.provider}.description`,
                                    })}
                                </p>
                            ),
                            onOk: () => history.goBack(),
                            okText: intl.formatMessage({
                                id: `button.back`,
                            }),
                        });
                    } else {
                        setConnectionPermission(connectionPermission);
                        setHubs(hubs);
                        form.setFieldsValue(connectionPermission);
                    }
                }
            } catch (error) {
                notificationService.displayError(error, intl);
            }
        };
        init();
    }, [form, history, intl, organizationId, params.id]);

    /*** METHODS ***/

    const save = async (values: any) => {
        try {
            setSaving(true);
            const sourceHub = hubs.find((h) => h.id === values.source.id);
            const targetHub = hubs.find((h) => h.id === values.target.id);
            let connectionPermissionUpdated: Connection = Object.assign({}, connectionPermission, values, {
                source: sourceHub,
                target: targetHub,
            });
            connectionPermissionUpdated = connectionPermissionUpdated.id
                ? await connectionApi.update(connectionPermissionUpdated)
                : await connectionApi.create(connectionPermissionUpdated);
            message.success(intl.formatMessage({ id: 'status.saved' }));
            setConnectionPermission(connectionPermissionUpdated);
        } catch (error) {
            notificationService.displayError(error, intl, [
                { status: 409, message: 'connectionPermission.status.duplicate' },
            ]);
        } finally {
            setSaving(false);
        }
    };

    const remove = async () => {
        try {
            setLoading('deleting');
            await connectionApi.delete(connectionPermission!);
            history.push(`/sync/permissions`);
        } catch (error) {
            notificationService.displayError(error, intl);
        } finally {
            setLoading(false);
        }
    };

    const showDeleteModal = () => {
        Modal.confirm({
            title: intl.formatMessage({ id: 'connectionPermission.delete' }),
            okText: intl.formatMessage({ id: 'button.yes' }),
            okType: 'primary',
            cancelText: intl.formatMessage({ id: 'button.no' }),
            okButtonProps: { loading: loading === 'deleting' },
            onOk: remove,
        });
    };

    /*** COMPONENTS ***/

    const sourceHubsOptions = hubs
        .sort((a, b) => stringService.sort(a.name, b.name))
        .map((hub) => (
            <Select.Option key={hub.id} value={hub.id}>
                {hub.provider} - {hub.name}
            </Select.Option>
        ));
    const targetHubsOptions = hubs
        .sort((a, b) => stringService.sort(a.name, b.name))
        .map((hub) => (
            <Select.Option key={hub.id} value={hub.id}>
                {hub.provider} - {hub.name}
            </Select.Option>
        ));

    const dropdownItems = [
        {
            key: 'clone',
            onClick: () => setConnectionPermissionCloneModalVisible(true),
            label: <FormattedMessage id="connectionPermission.button.clone" />,
            disabled:
                !connectionPermission || !connectionPermission.id || !rolesService.hasAllRoles(auth, ['ROLE_ADMIN']),
        },
        {
            key: 'delete',
            label: <FormattedMessage id="connectionPermission.button.delete" />,
            onClick: showDeleteModal,
            disabled: !connectionPermission || !connectionPermission.id,
        },
    ];

    return (
        <LayoutComponent pageId="connectionPermissions">
            <div className={styles.title}>
                <Breadcrumb className={styles.breadcrumb}>
                    <Breadcrumb.Item key="connectionPermissions">
                        <Link to="/sync/permissions">
                            <FormattedMessage id="connectionPermissions.title" />
                        </Link>
                    </Breadcrumb.Item>

                    {connectionPermission && connectionPermission.name ? (
                        <Breadcrumb.Item>{connectionPermission?.name}</Breadcrumb.Item>
                    ) : (
                        <Breadcrumb.Item>
                            <FormattedMessage id="connectionPermission.title" />
                        </Breadcrumb.Item>
                    )}
                </Breadcrumb>
                {connectionPermission && connectionPermission.cloned && (
                    <Tag className={styles.tag} color="orange">
                        <FormattedMessage id="connectionPermission.cloned" />
                    </Tag>
                )}
            </div>
            <Form form={form} onFinish={save} colon={false} labelAlign="left">
                <Row gutter={[28, 0]}>
                    <Col span={24}>
                        <Form.Item
                            label={<FormattedMessage id="connectionPermission.name" />}
                            name="name"
                            rules={[{ required: true, message: <FormattedMessage id="status.mandatory" /> }]}
                            labelCol={{ span: 5 }}
                            wrapperCol={{ span: 19 }}
                        >
                            <Input maxLength={100} size="large" />
                        </Form.Item>
                    </Col>
                </Row>
                <Row gutter={[28, 0]}>
                    <Col span={24}>
                        <Form.Item
                            label={<FormattedMessage id="connectionPermission.description" />}
                            name="description"
                            labelCol={{ span: 5 }}
                            wrapperCol={{ span: 19 }}
                        >
                            <TextArea maxLength={300} size="large" rows={3} />
                        </Form.Item>
                    </Col>
                </Row>
                <Row gutter={[28, 0]}>
                    <Col span={24}>
                        <Form.Item
                            label={<FormattedMessage id="connectionPermission.source" />}
                            name={['source', 'id']}
                            rules={[{ required: true, message: <FormattedMessage id="status.mandatory" /> }]}
                            labelCol={{ span: 5 }}
                            wrapperCol={{ span: 7 }}
                        >
                            <Select size="large" disabled={connectionPermission && !!connectionPermission.id}>
                                {sourceHubsOptions}
                            </Select>
                        </Form.Item>
                    </Col>
                </Row>
                <Row gutter={[28, 0]}>
                    <Col span={24}>
                        <Form.Item
                            label={<FormattedMessage id="connectionPermission.target" />}
                            name={['target', 'id']}
                            rules={[{ required: true, message: <FormattedMessage id="status.mandatory" /> }]}
                            labelCol={{ span: 5 }}
                            wrapperCol={{ span: 7 }}
                        >
                            <Select size="large" disabled={connectionPermission && !!connectionPermission.id}>
                                {targetHubsOptions}
                            </Select>
                        </Form.Item>
                    </Col>
                </Row>

                <Form.Item className="buttons">
                    <Space>
                        <Button
                            type="primary"
                            htmlType="submit"
                            size="large"
                            icon={<Icon component={SaveSvg} />}
                            loading={saving}
                        >
                            <FormattedMessage id="button.save" tagName="span" />
                        </Button>
                        <Link to="/sync/permissions">
                            <Button type="text" size="large">
                                <FormattedMessage id="button.back" tagName="span" />
                            </Button>
                        </Link>
                    </Space>
                    <Dropdown
                        trigger={['click']}
                        placement="bottomRight"
                        menu={{ items: dropdownItems }}
                        disabled={!connectionPermission || !connectionPermission.id}
                        className={styles.otherActions}
                    >
                        <Button size="large" ghost={true} type="primary">
                            <FormattedMessage id="button.otherActions" /> <DownOutlined />
                        </Button>
                    </Dropdown>
                </Form.Item>
            </Form>
            {connectionPermissionCloneModalVisible && connectionPermission && connectionPermission.id && (
                <ConnectionCloneModal
                    connectionId={connectionPermission.id}
                    onSave={() => setConnectionPermissionCloneModalVisible(false)}
                    onCancel={() => setConnectionPermissionCloneModalVisible(false)}
                />
            )}
            <ConnectionPermissionTabsComponent connectionPermission={connectionPermission} />
        </LayoutComponent>
    );
};

export default ConnectionPermissionPage;

type ParamsType = { id: string };

const ConnectionPermissionTabsComponent = (props: TabsProps): React.ReactElement => {
    const { connectionPermission } = props;
    return (
        <Tabs
            defaultActiveKey="links"
            size="large"
            items={[
                {
                    key: 'links',
                    label: <FormattedMessage id="connectionPermission.links" />,
                    children: connectionPermission && connectionPermission.id && (
                        <ConnectionPermissionLinksComponent connectionPermission={connectionPermission} />
                    ),
                },
                {
                    key: 'schedules',
                    label: <FormattedMessage id="connectionPermission.schedules" />,
                    children: connectionPermission && connectionPermission.id && (
                        <ConnectionSchedulesComponent connection={connectionPermission} />
                    ),
                },
                {
                    key: 'tasks',
                    label: <FormattedMessage id="connectionPermission.tasks" />,
                    children: connectionPermission && connectionPermission.id && (
                        <ConnectionTasksComponent connection={connectionPermission} />
                    ),
                },
            ]}
        />
    );
};

interface TabsProps {
    connectionPermission?: Connection;
}
